import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useUserStore } from '@/entities/user/store';
import { extractErrorMessage } from '@/shared/utils/apiErrors';
import apiClient from '@/api';

export const INVITE_TOKEN_KEY = 'biasafe_invite_token';

/**
 * Landing page for a team-invitation accept link (`/accept-invitation?token=…`).
 *
 * - Stashes the token so it survives the sign-up bounce.
 * - Signed in → accept immediately, then hard-navigate so the app re-bootstraps
 *   against the (now inviting) org.
 * - Not signed in → send them to sign up with the invited email; AuthLayout
 *   completes the pending invite once they're authenticated.
 */
export function AcceptInvitationPage() {
  const navigate = useNavigate();
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    const urlToken = new URLSearchParams(window.location.search).get('token');
    if (urlToken) sessionStorage.setItem(INVITE_TOKEN_KEY, urlToken);
    const token = sessionStorage.getItem(INVITE_TOKEN_KEY);

    if (!token) {
      setError('This invitation link is missing its token.');
      return;
    }
    if (!isAuthenticated) {
      navigate({ to: '/signup', replace: true });
      return;
    }
    if (ran.current) return;
    ran.current = true;
    void apiClient
      .post('/team/invitations/accept', { token })
      .then(() => {
        sessionStorage.removeItem(INVITE_TOKEN_KEY);
        window.location.assign('/strategies');
      })
      .catch((err) =>
        setError(extractErrorMessage(err, 'Could not accept this invitation.')),
      );
  }, [isAuthenticated, navigate]);

  return (
    <div className="light flex min-h-screen items-center justify-center bg-white px-6">
      <div className="max-w-sm text-center">
        {error ? (
          <>
            <h1 className="text-lg font-medium text-[#121212]">
              Invitation problem
            </h1>
            <p className="mt-2 text-sm text-[#ff4d4f]">{error}</p>
            <button
              type="button"
              onClick={() => navigate({ to: '/login', replace: true })}
              className="mt-4 rounded-lg bg-[#121212] px-4 py-2 text-sm text-white"
            >
              Go to sign in
            </button>
          </>
        ) : (
          <p className="text-sm text-[#3d3d3d]">Accepting your invitation…</p>
        )}
      </div>
    </div>
  );
}
